/**
 * Cost tracking and optimization types
 */

export type CostProvider = 
  | 'openai' 
  | 'mapbox' 
  | 'google-places' 
  | 'weather' 
  | 'stripe' 
  | 'firebase' 
  | 'redis'

export interface CostEntry {
  id: string
  provider: CostProvider
  service: string // Ex: "gpt-4", "directions", "geocoding"
  cost: number // In USD cents
  usage: number // API calls, tokens, reads, etc.
  unit: string // "tokens", "requests", "reads", "writes"
  userId?: string
  feature?: string // Ex: "ai-planner", "map-search", "poi-discovery"
  timestamp: Date
  metadata?: Record<string, unknown>
}

export interface CostSummary {
  total: number // Total cost in USD cents
  byProvider: Record<CostProvider, {
    cost: number
    usage: number
    percentage: number
  }>
  byFeature: Record<string, {
    cost: number
    usage: number
    percentage: number
  }>
  topUsers: Array<{
    userId: string
    userName: string
    cost: number
    usage: number
  }>
  trend: {
    current: number // This period
    previous: number // Previous period
    change: number // Percentage change
  }
}

export interface CostBudget {
  id: string
  provider: CostProvider
  limit: number // Monthly limit in USD cents
  spent: number // Current spent
  alertThreshold: number // Alert at X% (ex: 80)
  period: 'daily' | 'weekly' | 'monthly'
  enabled: boolean
}

export interface CostAlert {
  id: string
  type: 'budget' | 'spike' | 'anomaly' | 'quota'
  severity: 'low' | 'medium' | 'high' | 'critical'
  provider: CostProvider
  message: string
  details: {
    current: number
    threshold?: number
    expected?: number
    userId?: string
  }
  timestamp: Date
  acknowledged: boolean
}

export interface CostOptimizationSuggestion {
  id: string
  type: 'caching' | 'batching' | 'rate-limiting' | 'plan-change' | 'feature-disable'
  priority: 'low' | 'medium' | 'high'
  provider: CostProvider
  title: string
  description: string
  estimatedSavings: number // USD cents per month
  effort: 'low' | 'medium' | 'high'
  impact: number // 1-10 score
  actionable: boolean
  action?: {
    type: string
    params: Record<string, unknown>
  }
}

export interface CostAnalytics {
  period: 'day' | 'week' | 'month'
  summary: CostSummary
  budgets: CostBudget[]
  alerts: CostAlert[]
  suggestions: CostOptimizationSuggestion[]
  forecast: {
    nextMonth: number
    confidence: number
    based: string // "historical", "trend", "ml"
  }
  efficiency: {
    costPerUser: number
    costPerRequest: number
    cacheHitRate: number
    wastedCost: number // Errors, retries, etc.
  }
}

// Pricing constants (update as needed)
export const COST_PRICING = {
  openai: {
    'gpt-4o-mini': {
      input: 0.00015, // per 1K tokens
      output: 0.0006
    },
    'gpt-4o': {
      input: 0.005, // per 1K tokens
      output: 0.015
    },
    'gpt-4-turbo': {
      input: 0.01, // per 1K tokens
      output: 0.03
    },
    'gpt-4': {
      input: 0.03,
      output: 0.06
    },
    'gpt-3.5-turbo': {
      input: 0.0005,
      output: 0.0015
    },
    embedding: 0.0001 // per 1K tokens
  },
  mapbox: {
    directions: 0.006, // per request
    geocoding: 0.005,
    static: 0.002,
    tiles: 0.0005 // per 1K tiles
  },
  googlePlaces: {
    search: 0.032, // per request
    details: 0.017,
    autocomplete: 0.00283
  },
  weather: {
    current: 0.0001, // OpenWeather per call
    forecast: 0.0001,
    history: 0.0001
  },
  stripe: {
    transaction: 0.029, // 2.9% + $0.30
    fixed: 30
  },
  firebase: {
    read: 0.00006, // per 100K
    write: 0.00018,
    delete: 0.00002
  },
  redis: {
    upstash: {
      command: 0.0002, // per 10K commands
      storage: 0.25 // per GB-month
    }
  }
} as const

export type CostPricing = typeof COST_PRICING
